import { useMemo, useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useOutletContext } from 'react-router-dom'
import { Virtuoso } from 'react-virtuoso'

import SearchFilter from '@App/components/SearchFilter/SearchFilter'
import Dropdown from '@App/components/Dropdown/Dropdown'
import { setClickedItem } from '@App/pages/MosquitoObservations/mosquito-observations.slice'

import MosquitoObservationTabs from '../MosquitoObservationTabs/MosquitoObservationTabs'

import MosquitoObservationsDetectionListItem from './MosquitoObservationsDetectionListItem/MosquitoObservationsDetectionListItem'

import './mosquito-observations-detection-list.styl'

function MosquitoObservationsDetectionList() {
  const dispatch = useDispatch()
  const { map, observations = [], mosquitoSpecies = [], isLoading } = useOutletContext()

  const [trapSearchValue, setTrapSearchValue] = useState('')
  const [trapSortValue, setTrapSortValue] = useState(null)
  const clickedItem = useSelector(state => state.mosquitoDetection.clickedItem)
  const trapsListRef = useRef(null)

  const normalizeDeviceName = value => String(value || '').trim().toLowerCase()
  const getObservationName = observation => (
    observation?.deviceName
    || observation?.properties?.name
    || observation?.properties?.client_name
    || 'Unknown'
  )
  const getObservationId = observation => (
    observation?.deviceId
    || observation?.trapId
    || observation?.id
    || observation?.properties?.locationKey
    || observation?.properties?.client_name
  )
  const getObservationTime = observation => (
    observation?.properties?.latestRecordTime
    || 0
  )
  const getObservationStats = observation => (
    Array.isArray(observation?.stats)
      ? observation.stats
      : Array.isArray(observation?.data?.stats) ? observation.data.stats : []
  )

  const trapSortValues = [
    { id: 'name', label: 'Name' },
    { id: 'lastActivity', label: 'Last activity' },
    { id: 'sample', label: 'Samples' },
  ]

  const filteredAndSortedDetections = useMemo(() => {
    const filtered = trapSearchValue
      ? observations.filter((observation) => {
          console.log(
            normalizeDeviceName(getObservationName(observation)),
            normalizeDeviceName(trapSearchValue),
          )
          return normalizeDeviceName(getObservationName(observation)).includes(normalizeDeviceName(trapSearchValue))
        })
      : observations

    return [...filtered].sort((a, b) => {
      if (trapSortValue?.id === 'name') {
        const nameA = getObservationName(a)
        const nameB = getObservationName(b)
        return nameA.localeCompare(nameB)
      }
      else if (trapSortValue?.id === 'lastActivity') {
        const timeA = new Date(getObservationTime(a)).getTime()
        const timeB = new Date(getObservationTime(b)).getTime()
        return timeB - timeA
      }
      else if (trapSortValue?.id === 'sample') {
        const countA = getObservationStats(a).reduce((sum, item) => sum + (item.count || 0), 0)
        const countB = getObservationStats(b).reduce((sum, item) => sum + (item.count || 0), 0)
        return countB - countA
      }
      return 0
    })
  }, [observations, trapSearchValue, trapSortValue])

  useEffect(() => {
    if (!clickedItem) return
    const clickedIndex = filteredAndSortedDetections.findIndex((observation) => {
      return getObservationId(observation) === getObservationId(clickedItem)
    })

    if (clickedIndex >= 0 && trapsListRef.current) {
      trapsListRef.current.scrollToIndex({
        index: clickedIndex,
        align: 'center',
        behavior: filteredAndSortedDetections.length < 20 ? 'smooth' : 'auto',
      })
      dispatch(setClickedItem(null))
    }
  }, [clickedItem, dispatch, filteredAndSortedDetections])

  return (
    <>
      <MosquitoObservationTabs />
      <hr />
      <div className="mosquito-observations-detection-list">
        <div className="mosquito-observations-detection-list__head">
          <div className="mosquito-observations-detection-list__head--filter">
            <SearchFilter
              value={trapSearchValue}
              setValue={setTrapSearchValue}
            />
          </div>
          <div className="mosquito-observations-detection-list__head--content">
            <Dropdown
              searchOptions={trapSortValues}
              value={trapSortValue}
              setValue={setTrapSortValue}
            />
          </div>
        </div>
        <div className="mosquito-observations-detection-list__content">
          {!isLoading && filteredAndSortedDetections.length !== 0
            ? (
                <Virtuoso
                  ref={trapsListRef}
                  className="mosquito-observations-detection-list__virtualized"
                  data={filteredAndSortedDetections}
                  increaseViewportBy={400}
                  computeItemKey={(index, trap) => `${getObservationId(trap) || index}`}
                  itemContent={(_index, trap) => (
                    <div className="mosquito-observations-detection-list__row">
                      <MosquitoObservationsDetectionListItem
                        map={map}
                        trap={trap}
                        mosquitoSpecies={mosquitoSpecies}
                      />
                    </div>
                  )}
                />
              )
            : (
                <p className="mosquito-observations-detection-list__no-data-message">
                  {isLoading ? 'Loading mosquito trap detections...' : 'No mosquito trap detections found for the selected filters.'}
                </p>
              )}
        </div>
      </div>
    </>
  )
}

export default MosquitoObservationsDetectionList
